import PageAnimator from '../../components/ui/PageAnimator';
import Tooltip from '../../components/ui/Tooltip';
import React, { useState, useEffect } from "react";
import { 
  FileSearch, 
  CheckCircle, 
  User, 
  ArrowRight, 
  AlertTriangle, 
  Building2,
  Users,
  Search
} from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import MiniLoader from "../../components/MiniLoader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/Badge";
import Input from "../../components/ui/Input";
import PaginationFooter from "../../components/ui/PaginationFooter";

export default function AuditDashboard() {
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState({ total: 0, pending: 0, completed: 0, kycAlerts: 0 });
  const [loading, setLoading] = useState(true);
  const [partyTypeFilter, setPartyTypeFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, totalItems: 0 });
  const navigate = useNavigate();

  const fetchAudits = async (pageToFetch = page) => {
    setLoading(true);
    try {
      const res = await api.get("/advisory-audits", {
        params: {
          partyType: partyTypeFilter !== "ALL" ? partyTypeFilter : undefined,
          search: search.trim() || undefined,
          page: pageToFetch,
          pageSize: 10
        }
      });
      setItems(res.data.items || []);
      setPagination(res.data.pagination || { page: pageToFetch, pageSize: 10, totalItems: 0 });
      if (res.data.kpis) {
        setKpis(res.data.kpis);
      }
    } catch (error) {
      console.error("Erreur de chargement des audits :", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyTypeFilter, page]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (page === 1) {
      fetchAudits(1);
    } else {
      setPage(1);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
  };

  const getRiskBadge = (riskLevel) => {
    switch (riskLevel) {
      case "HIGH":
      case "Dynamique":
        return <Badge variant="destructive">Dynamique</Badge>;
      case "MEDIUM":
      case "Equilibre":
      case "Équilibré":
        return <Badge variant="warning">Équilibré</Badge>;
      case "LOW":
      case "Prudent":
        return <Badge variant="success">Prudent</Badge>;
      default:
        return <Badge variant="neutral">Non défini</Badge>;
    }
  };

  return (
    <div className="p-4 sm:p-8 bg-workspace min-h-screen">
      <PageAnimator>
        {/* Header Standardise */}
        <div
          className="
            relative z-20
            flex shrink-0 flex-col
            gap-6
            overflow-visible
            workspace-card
            bg-cover bg-center bg-no-repeat
            p-4 sm:p-6
            mb-8
            xl:flex-row xl:items-center xl:justify-between
          "
          style={{ backgroundImage: "url('/assets/backgrounds/castle_bg.png')" }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 rounded-[var(--hp-radius-lg)] bg-overlay"
          />

          <div className="relative z-10 flex min-w-0 shrink-0 flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary text-inverse shadow-hp-sm">
              <FileSearch className="h-6 w-6" />
            </div>

            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-inverse">
                Audits Patrimoniaux
              </h1>
              <p className="mt-1 text-sm text-inverse opacity-80">
                Tableau de bord de pilotage des audits et KYC de vos clients (Foyers & Sociétés).
              </p>
            </div>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="relative z-10 grid w-full grid-cols-1 gap-2 sm:grid-cols-3 xl:w-auto"
          >
            <motion.div variants={itemVariants} className="rounded-lg border border-white/20 bg-black/20 px-3 py-2 text-inverse">
              <p className="text-xs opacity-80">À planifier / en cours</p>
              <p className="text-lg font-bold">{kpis.pending}</p>
            </motion.div>
            <motion.div variants={itemVariants} className="rounded-lg border border-white/20 bg-black/20 px-3 py-2 text-inverse">
              <p className="text-xs opacity-80">Audits terminés</p>
              <p className="text-lg font-bold">{kpis.completed}</p>
            </motion.div>
            <motion.div variants={itemVariants} className="rounded-lg border border-white/20 bg-black/20 px-3 py-2 text-inverse">
              <p className="text-xs opacity-80">Alertes KYC</p>
              <p className="text-lg font-bold">{kpis.kycAlerts}</p>
            </motion.div>
          </motion.div>
        </div>

        {/* Tableau des Audits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="workspace-card-elevated min-h-0 overflow-hidden"
        >
          <div className="flex flex-col items-stretch justify-between gap-4 border-b border-workspace bg-surface px-6 py-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2 rounded-xl border border-[var(--hp-color-border-muted)] bg-surface p-1">
              <Button
                variant={partyTypeFilter === "ALL" ? "default" : "ghost"}
                size="sm"
                onClick={() => { setPage(1); setPartyTypeFilter("ALL"); }}
              >
                Tous ({kpis.total})
              </Button>
              <Button
                variant={partyTypeFilter === "INDIVIDUAL" ? "default" : "ghost"}
                size="sm"
                onClick={() => { setPage(1); setPartyTypeFilter("INDIVIDUAL"); }}
              >
                <Users className="mr-1.5 h-3.5 w-3.5" /> Foyers
              </Button>
              <Button
                variant={partyTypeFilter === "ORGANIZATION" ? "default" : "ghost"}
                size="sm"
                onClick={() => { setPage(1); setPartyTypeFilter("ORGANIZATION"); }}
              >
                <Building2 className="mr-1.5 h-3.5 w-3.5" /> Sociétés
              </Button>
            </div>

            <form onSubmit={handleSearchSubmit} className="flex w-full items-center gap-2 sm:w-auto">
              <Input
                type="text"
                placeholder="Rechercher par nom..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full sm:w-64"
              />
              <Button type="submit" variant="secondary" size="sm" aria-label="Rechercher">
                <Search className="h-4 w-4" />
              </Button>
            </form>
          </div>
          
          <table className="min-w-full divide-y divide-[var(--hp-color-border-muted)]">
            <thead className="bg-surface sticky top-0 z-10 border-b border-[var(--hp-color-border)]">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-subtle uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" /> Client / Entité
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-subtle uppercase tracking-wider">Type</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-subtle uppercase tracking-wider">Contact / Référence</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-subtle uppercase tracking-wider">Profil Risque</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-subtle uppercase tracking-wider">Statut Audit</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-subtle uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-[var(--hp-color-border-muted)]">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12">
                    <div className="flex justify-center">
                      <MiniLoader />
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12">
                    <div className="text-center text-sm text-subtle flex flex-col items-center">
                      <CheckCircle className="w-10 h-10 text-[var(--hp-success-500)] mb-2" />
                      Aucun dossier trouvé pour ces critères.
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  return (
                    <tr key={item.id} className="hover:bg-hover transition-colors group">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-primary">{item.nom}</span>
                          {item.hasKycAlert && (
                            <Tooltip content={`Alerte KYC : ${item.kycStatus === 'EXPIRE' ? 'Expiré' : item.kycStatus === 'MANQUANT' ? 'Document manquant' : 'À réviser'}`}>
                              <span className="text-[var(--hp-warning-600)]">
                                <AlertTriangle className="w-4 h-4" />
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-subtle">
                        <Badge variant="neutral">
                          {item.partyType === "ORGANIZATION" ? "Société" : "Foyer"}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-subtle">
                        {item.email || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                        {getRiskBadge(item.profilInvestisseur)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                        <Badge variant={item.statutAudit === "COMPLETED" ? "success" : item.statutAudit === "IN_PROGRESS" ? "info" : "warning"}>
                          {item.statutAudit === "COMPLETED" ? "Terminé" : item.statutAudit === "IN_PROGRESS" ? "En cours" : "À planifier"}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              const route = item.partyType === "ORGANIZATION" 
                                ? `/prospects/societes/${item.partyId}`
                                : `/prospects/familles/${item.partyId}`;
                              navigate(route);
                            }}
                          >
                            Consulter <ArrowRight className="w-3.5 h-3.5 ml-1" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <PaginationFooter
            page={pagination.page || page}
            pageSize={pagination.pageSize || 10}
            totalItems={pagination.totalItems || 0}
            onPageChange={setPage}
            disabled={loading}
            ariaLabel="Pagination des audits"
          />
        </motion.div>
      </PageAnimator>
    </div>
  );
}
